import { Component, input, output } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet-contextmenu';
import { FibreBuildingSite } from '../../../models/fibre-building-site';
import { EligibilitySite } from '../../../models/eligibility-site';
import { EXTERNAL_MAP_PROVIDERS, ExternalMapProvidersService } from '../../../services/external-map-providers-service';
import { TechnicalEligibilityApiService } from '../../../services/technical-eligibility-api-service';
import { FibreBuildingApiService } from '../../../services/fibre-building-api-service';
import { SnackBarService } from '../../../services/snack-bar-service';

@Component({
  selector: 'app-map-component',
  imports: [],
  templateUrl: './map-component.html',
  styleUrl: './map-component.css',
})
export class MapComponent {
  public readonly selectedSiteIndex = input<any>();

  public readonly onMoveSiteMarker = output<any>();
  public readonly onClickSiteMarker = output<any>();
  public readonly onClickFibreBuildingSiteMarker = output<any>();

  private map!: L.Map;

  public siteMarkersMap = new Map<number, L.Marker>();
  
  private readonly fibreBuildingSiteMap = new Map<number, any>();
  private fibreBuildingSiteMarkers: L.CircleMarker[] = [];

  private readonly siteLayerGroup = new L.LayerGroup();
  private readonly fibreBuildingSiteLayerGroup = new L.LayerGroup();

  constructor(
    private readonly technicalEligibilityApiService: TechnicalEligibilityApiService,
    private readonly fibreBuildingApiService: FibreBuildingApiService,
    private readonly snackBar: SnackBarService
  ) {}

  contextMenuMoveMarkerCallback(e: L.ContextMenuItemClickEvent) {
    if (this.selectedSiteIndex() == null || !e.latlng) {
      // No site selected / no map coords
      return;
    }

    this.moveSiteMarker(this.selectedSiteIndex(), e.latlng.lat, e.latlng.lng)
  }


  contextMenuOpenExternalProviderMap(provider: string, e: L.ContextMenuItemClickEvent) {
    if (!e.latlng) {
      return;
    }

    const externalMapProviderUrl = ExternalMapProvidersService.getExternalMapProviderUrl(provider, e.latlng.lat, e.latlng.lng);

    if (!externalMapProviderUrl) {
      return;
    }
    window.open(externalMapProviderUrl, '_blank');
  }

  private generateContextMenuItems() {
    const contextMenuItems: L.ContextMenuItem[] = [{
      text: 'Deplacer le marqueur ici',
      callback: (e) => this.contextMenuMoveMarkerCallback(e)
    }, {
      text: '',
      separator: true
    }];

    Object.keys(EXTERNAL_MAP_PROVIDERS).forEach(provider => {
      contextMenuItems.push({
        text: EXTERNAL_MAP_PROVIDERS[provider].label,
        icon: '/assets/new-window.png',
        callback: (e) => this.contextMenuOpenExternalProviderMap(provider, e)
      })
    });

    return contextMenuItems;
  }

  ngAfterViewInit(): void {
    this.map = L.map('map', {
      center: [ 46.74494710750971, 2.4327105787947754 ],
      zoom: 7,
      layers: [this.siteLayerGroup, this.fibreBuildingSiteLayerGroup],
      keyboardPanDelta: 0,
      contextmenu: true,
      contextmenuItems: this.generateContextMenuItems()
    });

    this.initializeTileLayers();

    this.map.on('baselayerchange', (e) => localStorage.setItem('mapBaseLayer', e.name));
  }

  initializeTileLayers(): void {
    const osmTileLayer = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        minZoom: 3,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    });

    const googleStreetTileLayer = L.tileLayer('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',{
      maxZoom: 20,
      minZoom: 3,
      subdomains:['mt0','mt1','mt2','mt3'],
      attribution: '&copy; <a href="https://www.google.com">Google</a>'
    });

    const googleSatelliteTileLayer = L.tileLayer('https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',{
      maxZoom: 20,
      minZoom: 3,
      subdomains:['mt0','mt1','mt2','mt3'],
      attribution: '&copy; <a href="https://www.google.com">Google</a>'
    });

    const googleHybridTileLayer = L.tileLayer('https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',{
      maxZoom: 20,
      minZoom: 3,
      subdomains:['mt0','mt1','mt2','mt3'],
      attribution: '&copy; <a href="https://www.google.com">Google</a>'
    });

    const googleTerrainTileLayer = L.tileLayer('https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',{
      maxZoom: 20,
      minZoom: 3,
      subdomains:['mt0','mt1','mt2','mt3'],
      attribution: '&copy; <a href="https://www.google.com">Google</a>'
    });

    const bdOrthoTileLayer = L.tileLayer('https://data.geopf.fr/tms/1.0.0/ORTHOIMAGERY.ORTHOPHOTOS/{z}/{x}/{y}.jpeg', {
      maxZoom: 19,
      minZoom: 3,
      attribution: '&copy; <a href="https://geoservices.ign.fr/">IGN</a>'
    });

    const planIgnTileLayer = L.tileLayer('https://data.geopf.fr/tms/1.0.0/GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2/{z}/{x}/{y}.png', {
      maxZoom: 19,
      minZoom: 0,
      attribution: '&copy; <a href="https://geoservices.ign.fr/">IGN</a>'
    });

    const cadastreTileLayer = L.tileLayer('https://data.geopf.fr/tms/1.0.0/CADASTRALPARCELS.PARCELLAIRE_EXPRESS/{z}/{x}/{y}.png', {
      maxZoom: 19,
      minZoom: 0,
      attribution: '&copy; <a href="https://geoservices.ign.fr/">IGN</a>'
    });

    const tileLayers = {
      "OpenStreetMap": osmTileLayer,
      //"OpenStreetMap FR": osmFrTileLayer,
      "Google Street": googleStreetTileLayer,
      "Google Satellite": googleSatelliteTileLayer,
      "Google Hybrid": googleHybridTileLayer,
      "Google Terrain": googleTerrainTileLayer,
      "BD Ortho": bdOrthoTileLayer,
      "Plan IGN": planIgnTileLayer,
      "Cadastre": cadastreTileLayer
    };

    type TileLayerName = keyof typeof tileLayers;

    const layerControl = L.control.layers(tileLayers, {
      "Sites": this.siteLayerGroup,
      "Immeubles ARCEP": this.fibreBuildingSiteLayerGroup
    });

    layerControl.addTo(this.map);

    const savedMapBaseLayer: string = localStorage.getItem('mapBaseLayer') ?? "OpenStreetMap";
    const tileLayerName: TileLayerName = savedMapBaseLayer in tileLayers ? savedMapBaseLayer as TileLayerName : "OpenStreetMap";
    const tileLayer: L.TileLayer = tileLayers[tileLayerName];

    tileLayer.addTo(this.map);
  }

  goToSite(site: EligibilitySite): void {
    if (!site?.validatedAddress?.coordinates_latitude || !site?.validatedAddress?.coordinates_longitude) {
      return;
    }

    this.map.setView([site.validatedAddress.coordinates_latitude, site.validatedAddress.coordinates_longitude], 18);
  }

  private getIconByColour(colour: string) {
    return L.icon({
      iconUrl: `/img/marker-icon-2x-${colour}-x.png`,
      shadowUrl: "/img/marker-shadow.png",
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });
  }

  getIconByState(state?: string) {
    switch (state) {
      case 'confirmed':
        return this.getIconByColour('green');
      case 'unsure':
        return this.getIconByColour('orange');
      case 'not_found':
        return this.getIconByColour('red');
      default:
        return this.getIconByColour('blue');
    }
  }

  createSiteMarker(siteId: number, markerTitle: string, lat: any, lng: any): L.Marker {
    const icon = this.getIconByColour('blue');

    const marker = L.marker([lat, lng], {
      draggable: true,
      icon: icon,
      zIndexOffset: 1000,
      contextmenu: false,
      contextmenuItems: []
    });

    marker.addTo(this.siteLayerGroup);

    marker.bindTooltip(markerTitle);

    marker.on('click', (event: any) => {
      this.onClickSiteMarker.emit(siteId);
    });

    marker.on('moveend', (event: any) => {
      this.onMoveSiteMarker.emit({
        siteIndex: siteId,
        latitude: event.target.getLatLng().lat,
        longitude: event.target.getLatLng().lng
      });
    });

    return marker;
  }

  addSiteMarker(siteId: number, site: EligibilitySite): void {
    if (!site?.validatedAddress?.coordinates_latitude || !site?.validatedAddress?.coordinates_longitude) {
      return;
    }

    if (this.siteMarkersMap.get(siteId)) {
      this.removeSiteMarker(siteId);
    }

    const markerTitle = `Site #${siteId}` + (site.name ? ` - ${site.name}` : '');

    const newMarker = this.createSiteMarker(siteId, markerTitle, site.validatedAddress?.coordinates_latitude, site.validatedAddress?.coordinates_longitude);

    this.siteMarkersMap.set(siteId, newMarker);
  }

  public addSiteMarkers(sites: any[]) {
    sites.forEach((site: EligibilitySite) => { 
      this.addSiteMarker(site.id, site);
    });
  }

  public removeSiteMarker(siteId: number): void {
    const oldMarker = this.siteMarkersMap.get(siteId);

    if (oldMarker) {
      oldMarker.remove();
    }

    this.siteMarkersMap.delete(siteId);
  }

  public moveSiteMarker(siteId: number, latitude: number, longitude: number, bypassConfirmation: boolean = false, emitEvent = true): void {
    const siteMarker = this.siteMarkersMap.get(siteId);
    siteMarker?.setLatLng([latitude, longitude]);

    if (emitEvent === true) {
      this.onMoveSiteMarker.emit({
        siteIndex: siteId,
        latitude: latitude,
        longitude: longitude,
        bypassConfirmation: bypassConfirmation
      });
    }
  }

  public zoomToSiteMarkers() {
    if (this.siteMarkersMap.size === 0) {
      return;
    }

    const group = new L.FeatureGroup(Array.from(this.siteMarkersMap.values()));
    
    this.map.invalidateSize();
    this.map.fitBounds(group.getBounds());
  }

  public zoomToSiteMarker(index: any) {
    const siteMarker = this.siteMarkersMap.get(index);

    if (!siteMarker) {
      return;
    }

    this.map.invalidateSize();
    this.map.setView(siteMarker.getLatLng(), 18);
  }


  public clearSiteMarkers(): void {
    for (const marker of this.siteMarkersMap.values()) {
      marker.remove();
    }

    this.siteMarkersMap = new Map<number, L.Marker>();
  }

  getFibreBuildingSiteByIndex(index: number) {
    return this.fibreBuildingSiteMap.get(index);
  }

  getSiteFibreBuildings(site: EligibilitySite): void {
    if (!site?.validatedAddress?.coordinates_latitude || !site?.validatedAddress?.coordinates_longitude) {
      return;
    }

    this.clearFibreBuildingSiteMarkers();

    this.fibreBuildingApiService
      .getBuildingsByCoordinates(site.validatedAddress.coordinates_latitude, site.validatedAddress.coordinates_longitude)
      .subscribe({
        next: (fibreBuildingSites) => {
          this.fibreBuildingSiteMap.clear();

          fibreBuildingSites.forEach((fibreBuildingSite, index) => {
            this.fibreBuildingSiteMap.set(index, fibreBuildingSite);
          });

          this.addFibreBuildingSiteMarkers(Array.from(this.fibreBuildingSiteMap.values()));
        },
        error: () => {
          this.snackBar.error("Erreur rencontrée lors de la recuperation des immeubles FTTH à proximité.");
        }
      });
  }

  getFibreBuildingSiteMarkerColour(fibre_building_site: FibreBuildingSite) {
    // TODO : Add missing colours
    // Etudes réalisées : #9b9b9b
    // Programmé : #e0af00 => OK
    // Raccordable sur demande : #08beff => OK
    // Raccordable sur demande (en cours de déploiement) : #0846ff => OK
    // En cours de déploiement : #464646 => OK
    // Déployé (raccordable) :  => OK
    // Unknown : #ff0000 

    // Valeurs fichier ARCEP :
    // abandonne => cas assez rare
    // raccordable demande => Raccordable sur demande
    // rad en cours de deploiement => Raccordable sur demande (en cours de deploiement) ?
    // signe
    // cible => Programmé
    // en cours de deploiement => En cours de déploiement
    // deploye => Deployé

    switch(fibre_building_site.sourceAddress.fibre_building_state) {
      case 'deploye':
        return '#2d9a06';
      case 'raccordable demande':
        return '#08beff';
      case 'rad en cours de deploiement':
        return '#0846ff';
      case 'en cours de deploiement':
        return '#464646';
      case 'signe':
        return '#e0af00';
      case 'cible':
        return '#e0af00';
      default:
        return '#ff0000';
    }
  }

  getFibreBuildingSiteMarkerState(fibre_building_site: FibreBuildingSite) {
    switch(fibre_building_site.sourceAddress.fibre_building_state) {
      case 'deploye':
        return 'Deployé (raccordable)';
      case 'raccordable demande':
        return 'Raccordable sur demande';
      case 'rad en cours de deploiement':
        return 'Raccordable sur demande (en cours de déploiement)';
      case 'en cours de deploiement':
        return 'En cours de déploiement';
      case 'signe':
        return 'Programmé (Convention signée)';
      case 'cible':
        return 'Programmé';
      case 'abandonne':
        return 'Abandonné';
      default:
        return 'Inconnu';
    }
  }

  addFibreBuildingSiteMarker(siteIndex: any, fibre_building_site: FibreBuildingSite) {
    if (!fibre_building_site.sourceAddress.coordinates_latitude || !fibre_building_site.sourceAddress.coordinates_longitude) {
      return;
    }

    const marker = L
      .circleMarker([+fibre_building_site.sourceAddress.coordinates_latitude, +fibre_building_site.sourceAddress.coordinates_longitude], 
        {
          stroke: true,
          color: '#ffffff', // Stroke (border) color
          opacity: 1, // Stroke (border) opacity
          weight: 1, // Stroke (border) width
          radius: 6, // Circle radius
          fill: true,
          fillOpacity: 1,
          fillColor: this.getFibreBuildingSiteMarkerColour(fibre_building_site)
        });

    marker.bindTooltip(`
      <strong>Nom batiment</strong>: ${fibre_building_site.name ?? ' - '}<br>
      <strong>Code IMB</strong>: ${fibre_building_site.sourceAddress.fibre_building_code}<br>
      <strong>OI</strong>: ${fibre_building_site.sourceAddress.fibre_building_operator ? this.technicalEligibilityApiService.getInfrastructureOperatorByCode(fibre_building_site.sourceAddress.fibre_building_operator)?.name : ''}<br>
      <strong>Etat</strong>: ${this.getFibreBuildingSiteMarkerState(fibre_building_site)}<br>
      <strong>Adresse</strong>: ${fibre_building_site.sourceAddress.address_line_1}, ${fibre_building_site.sourceAddress.address_zip_code} ${fibre_building_site.sourceAddress.address_city}
    `);

    marker.addTo(this.fibreBuildingSiteLayerGroup);
    marker.on('click', (event: any) => {
      this.moveSiteMarker(this.selectedSiteIndex(), event.latlng.lat, event.latlng.lng, true, false);
      this.onClickFibreBuildingSiteMarker.emit(siteIndex); 
    });

    this.fibreBuildingSiteMarkers.push(marker);
  }

  addFibreBuildingSiteMarkers(fibreBuildingSites: any[]) {
    fibreBuildingSites.forEach((fibreBuildingSite, index) => this.addFibreBuildingSiteMarker(index, fibreBuildingSite));
  }

  clearFibreBuildingSiteMarkers() {
    this.fibreBuildingSiteMarkers.forEach(marker => marker.remove());

    this.fibreBuildingSiteMarkers = [];
  }
}
